import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import HeaderLayout from '../../../components/Layout/Header/HeaderLayout';
import BodyDetailProduct from '../../../components/products/productsDetails/BodyDetailProduct';
import { useGetExchangeRate } from '../../../hooks/useGetExchangeRate';
import { useGetExchangeRateHistory } from '../../../hooks/useGetExchangeRateHistory';
import useGetPriceRange from '../../../hooks/useGetPriceRange';
import useGetSecurityDividends from '../../../hooks/useGetSecurityDividends';
import { getRiskWarningFunds } from '../../../utils/getRiskWarning';
import {
  getCurrentDateFormatYYYYMMDD,
  getFirstElement,
  numberWithPoint,
  setMonthsDateFormatYYMMDD,
} from '../../../utils/utils';

const ProductsDetails = () => {
  const [active, setActive] = useState(12);
  const [currency, setCurrency] = React.useState('dolares');
  const [chartData, setChartData] = React.useState([]);
  const navigate = useNavigate();
  const { securitySelected } = useSelector(state => state.simulation);
  const user = useSelector(state => state.auth.user);
  const [userNotOB, setUserNotOB] = useState(false);

  const custom = JSON.parse(securitySelected.Custom);
  const risk = custom.preferences.riesgo.description;
  const motivations = getFirstElement(custom.preferences.motivaciones);
  const interests = getFirstElement(custom.preferences.intereses);
  const motivationsId = custom.preferences.motivaciones[0].id;
  const interestsId = custom.preferences.intereses[0].id;
  const description = custom.fund_details.description_fund;
  const riskID = custom.preferences.riesgo;
  const showRiskWarning = getRiskWarningFunds(user['custom:profile'], riskID.id);

  const onlyNumber = custom?.fund_details?.min_investment_amount
    ? String(custom.fund_details.min_investment_amount).replace(/[^\d]/g, '')
    : '';

  const config = [
    {
      key: 'min_investment_amount',
      label: 'Monto mínimo de inversión:',
      format: () => `$${numberWithPoint(Number.parseInt(onlyNumber))}`,
    },
    { key: 'currency', label: 'Moneda:' },
    { key: 'max_settle_date', label: 'Plazo de aporte:' },
    { key: 'dividend_payment', label: 'Paga dividendos:' },
    { key: 'plazo_rescates', label: 'Plazo de rescate:' },
    { key: 'serie', label: 'Serie:' },
    { key: 'admin_fee', label: 'Remuneración fija:' },
    { key: 'exit_fee', label: 'Comisión de rescate:' },
  ];

  const details = custom.fund_details;

  const conditions = config
    .filter(({ key }) => details[key])
    .map(({ key, label, format }) => ({
      data: label,
      value: format ? format(details[key]) : details[key],
    }));

  const documents = [];

  const documentTypes = {
    Fact_Sheet: 'Brochure',
    Reglamento: 'Reglamento',
    Folleto_Regulatorio: 'Folleto Informativo',
  };

  if (custom.documents) {
    for (const [key, value] of Object.entries(custom.documents)) {
      const documentType = documentTypes[key];
      if (documentType) {
        documents.push({
          data: documentType,
          value,
        });
      }
    }
  }

  const [
    doGetPriceRange,
    { data: dataPriceRange, error: errorGetPriceRange, loading: loadingGetPriceRange },
  ] = useGetPriceRange();
  const successGetPriceRange = dataPriceRange?.getPriceRange || [];
  const [doGetSecurityDividends, { data: dataSecurityDividends }] = useGetSecurityDividends();
  const successGetSecurityDividends = dataSecurityDividends?.getSecurityDividends;

  const { data: dataGetExchangeRate, loading: loadingGetExchangeRate } = useGetExchangeRate();
  const { data: dataGetExchangeRateHistory, loading: loadingGetExchangeRateHistory } =
    useGetExchangeRateHistory({
      rowEnd: 365,
    });

  const successGetExchangeRateHistory = dataGetExchangeRateHistory?.getExchangeRateHistory;

  const onChangeMonths = months => {
    setActive(months);
    doGetPriceRange({
      variables: {
        data: {
          securityId: securitySelected.ID,
          from: setMonthsDateFormatYYMMDD(-months),
          to: getCurrentDateFormatYYYYMMDD(),
        },
      },
    });
  };

  const updateChartData = (priceData, exchangeRates) => {
    const convertedData = priceData?.map(priceItem => {
      const matchingRate = exchangeRates?.find(
        rateItem => rateItem?.EffectiveDate === priceItem?.EffectiveDate,
      );

      // si encontramos una tasa de cambio para esa fecha, dividimos el closePrice por esa tasa
      const updatedClosePrice = matchingRate
        ? priceItem.ClosePrice / matchingRate.Rate
        : priceItem.ClosePrice; // Si no hay tasa de cambio, dejamos el closePrice sin cambios

      return {
        ...priceItem,
        ClosePrice: updatedClosePrice,
      };
    });
    setChartData(convertedData);
  };

  const handleInvestment = () => {
    if (!userNotOB) {
      return navigate('/completeregistration');
    }
    if (userNotOB) {
      navigate(`/simulator/investment/${securitySelected.ID}`, {
        state: { fromCard: true },
      });
    }
  };

  const handleSimulation = () => {
    navigate(`/simulator/form/${securitySelected.ID}`);
  };
  const fileAs = {
    currency: custom.fund_details.currency,
    fileAs: 'FileAsName',
    fileAsComplete: securitySelected.Name.slice(4),
    risk: risk,
    motivation: motivations.split('-')[0],
    motivationId: motivationsId,
    interests: interests,
    interestsId: interestsId,
    products: true,
    profit: custom.fund_details.expected_return,
  };
  const isSimulatorFund = true;
  const dataChart = {
    fundName: securitySelected?.Name,
    profileName: custom?.preferences?.riesgo?.description,
  };

  useEffect(() => {
    if (
      user?.['custom:checkid-sign-doc'] &&
      user['custom:checkid-sign-doc'] === 'signature_success'
    ) {
      setUserNotOB(true);
    }
  }, [user]);

  return (
    <>
      <HeaderLayout
        fileAs={fileAs}
        type='product'
        isSimulatorFund={isSimulatorFund}
        dataChart={dataChart}
      />

      <BodyDetailProduct
        fileAs={fileAs}
        simulation='Simular'
        investmentMore='Invertir'
        doGetSecurityDividends={doGetSecurityDividends}
        securitySelected={securitySelected}
        description={description}
        conditions={conditions}
        onChangeMonths={onChangeMonths}
        active={active}
        loadingGetPriceRange={loadingGetPriceRange}
        loadingGetExchangeRate={loadingGetExchangeRate}
        errorGetPriceRange={errorGetPriceRange}
        currency={currency}
        custom={custom}
        chartData={chartData}
        dividends={successGetSecurityDividends}
        showLabels={true}
        successGetPriceRange={successGetPriceRange}
        setCurrency={setCurrency}
        updateChartData={updateChartData}
        documents={documents}
        showRiskWarning={showRiskWarning}
        handleInvestment={handleInvestment}
        handleInvestMore={handleInvestment}
        handleSimulation={handleSimulation}
        handleRescueInvestment={handleSimulation}
        successGetExchangeRateHistory={successGetExchangeRateHistory}
        loadingGetExchangeRateHistory={loadingGetExchangeRateHistory}
      />
    </>
  );
};

export default ProductsDetails;
